import React from 'react';
import { SimpleLineChart } from '../atoms/SimpleLineChart';
import { SimpleBarChart } from '../atoms/SimpleBarChart';
import { SimplePieChart } from '../atoms/SimplePieChart';
import { SimpleChartData, chartDefaults, ISimpleChartOptions, ISimpleBaseChart, ISimplePieChart } from './types';
import { TooltipLine } from '@/utils/charts';
import { NameType, Payload, ValueType } from 'recharts/types/component/DefaultTooltipContent';
import { cn } from '@/lib/utils';
import { ChartConfig } from '@/components/ui/chart';

export const SimpleChartEngine: React.FC<ISimpleBaseChart> = ({ type, ...rest }) => {
  const renderChart = () => {
    switch (type) {
      case 'line':
        return <SimpleLineChart type="line" {...rest} />;
      case 'bar':
        return <SimpleBarChart type="bar" {...rest} />;
      case 'pie':
        return <SimplePieChart type="pie" {...rest} />;
      default:
        return null;
    }
  };

  return renderChart();
};

const transformDataKey = (key: string) => {
  // Handle snake_case: replace underscores with spaces and capitalize first letter of each word
  if (key.includes('_')) {
    return key.replace(/_/g, ' ').replace(/\b\w/g, str => str.toUpperCase());
  }

  // Handle camelCase: add space before capital letters and capitalize first letter of each word
  return key.replace(/([A-Z])/g, ' $1').replace(/\b\w/g, str => str.toUpperCase());
}

export const AIChartEngineCustomTooltipContent = ({
  active,
  payload,
  options,
  config
}: {
  active?: boolean;
  payload?: Array<Payload<ValueType, NameType>>;
  chartData?: SimpleChartData;
  options: ISimpleChartOptions;
  config?: ChartConfig;
}) => {
  if (!active || !payload || !payload[0]) return null;

  const label = payload?.[0]?.payload?.name;

  return (
    <div className="rounded-lg border p-2 shadow-md bg-card-reverse text-card-reverse-foreground min-w-60 text-xs">
      <div className="text-sm font-medium mb-2">{label}</div>

      <div className="flex flex-col gap-2 ">
        {payload.map((item) => (
          <TooltipLine
            key={item?.dataKey as string | number | undefined}
            color={item?.color || item?.payload?.fill}
            label={config?.[item?.dataKey as string]?.label as string || transformDataKey(item?.name as string)}
            value={(
              <>
                {options?.tooltipValueFormatter ? options?.tooltipValueFormatter(Number(item?.value)) : item?.value}
              </>
            )}
          />
        ))}
      </div>
    </div>
  );
};

export const AIChartEngineCustomLegendContent = ({
  payload,
  verticalAlign = "bottom",
  className,
  config
}: {
  payload?: Array<Payload<ValueType, NameType>>;
  verticalAlign?: "top" | "bottom";
  className?: string;
  config?: ChartConfig;
}) => {
  return (
    <div
      className={cn(
        "flex items-center justify-center gap-4",
        verticalAlign === "top" ? "pb-3" : "pt-3",
        className
      )}
    >
      {payload?.map((item) => {
        const key = `${item?.dataKey || item?.value || "value"}`
        const itemLabel = config?.[item?.dataKey as string]?.label || transformDataKey((item?.value || item?.dataKey) as string)

        return (
          <div
            key={key}
            className={cn(
              "flex items-center gap-1.5 [&>svg]:h-3 [&>svg]:w-3 [&>svg]:text-muted-foreground text-muted-foreground"
            )}
          >
            <div
              className="size-2 shrink-0 rounded-xs"
              style={{
                backgroundColor: item.color,
              }}
            />
            {itemLabel}
          </div>
        )
      })}
    </div>
  );
};

export const generateChartParams = (simpleBaseChartProps: ISimpleBaseChart) => {
  const chartOptions = { ...chartDefaults.options, ...simpleBaseChartProps.options } as ISimpleChartOptions;
  const chartDataKeys = generateChartDataKeys(simpleBaseChartProps);
  const chartColors = [...(simpleBaseChartProps.colors || []), ...chartDefaults.colors];
  const chartConfig = generateChartConfig(simpleBaseChartProps, chartDataKeys, chartColors);

  return { chartDataKeys, chartColors, chartConfig, chartOptions };
}

const generateChartConfig = (simpleBaseChartProps: ISimpleBaseChart, dataKeys: string[], colors: string[]) => {
  return dataKeys.reduce((acc, key, index) => {
    acc[key] = {
      label: simpleBaseChartProps?.labels?.[key] || transformDataKey(key),
      color: colors[index],
    };
    return acc;
  }, {} as Record<string, { label: string; color: string }>);
}

const generateChartDataKeys = (simpleBaseChartProps: ISimpleBaseChart): string[] => {
  if (simpleBaseChartProps?.type === 'pie') {
    return simpleBaseChartProps?.data?.map((item) => item[(simpleBaseChartProps as ISimplePieChart)?.nameKey || 'name'] as string)
  }
  if (simpleBaseChartProps?.dataKeys) {
    return simpleBaseChartProps?.dataKeys;
  }
  return simpleBaseChartProps?.data?.[0] ? Object.keys(simpleBaseChartProps?.data?.[0])?.filter(key => key !== 'name') : [];
}